// Environment configuration
// This file provides a Jest-friendly way to access environment variables

const getEnvVar = (key: string, defaultValue?: string): string => {
  // Prefer import.meta.env first: Vite injects VITE_* there. In the browser, `process` is often
  // polyfilled and process.env exists but does not include VITE_* — reading process first returned
  // empty strings and skipped Firebase and other client env (regression).
  const meta = import.meta.env as Record<string, string | undefined>;
  const fromVite = meta[key];
  if (fromVite !== undefined && fromVite !== '') {
    return fromVite;
  }
  if (typeof process !== 'undefined' && process.env) {
    const fromProcess = process.env[key];
    if (fromProcess !== undefined && fromProcess !== '') {
      return fromProcess;
    }
  }
  return defaultValue || '';
};

/**
 * Variant of getEnvVar that treats an explicitly provided empty string as a valid value.
 * This is important for "disable by setting empty" flags like excluded domain lists.
 */
const getEnvVarAllowEmpty = (key: string, defaultValue?: string): string => {
  const meta = import.meta.env as Record<string, string | undefined>;
  if (Object.prototype.hasOwnProperty.call(meta, key)) {
    return meta[key] ?? '';
  }
  if (typeof process !== 'undefined' && process.env) {
    if (Object.prototype.hasOwnProperty.call(process.env, key)) {
      return process.env[key] ?? '';
    }
  }
  return defaultValue || '';
};

const apiBaseUrl = getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000');
const externalApiBaseUrl = getEnvVar(
  'VITE_EXTERNAL_API_BASE_URL',
  `${apiBaseUrl.replace(/\/$/, '')}/v1`,
);

export const ENV = {
  VITE_AUTH_PROVIDER: getEnvVar('VITE_AUTH_PROVIDER', 'google_identity_platform'),
  /**
   * Optional app version for analytics/debugging.
   * Suggested: set `VITE_APP_VERSION` at build time (e.g. git sha or semver).
   */
  VITE_APP_VERSION: getEnvVar('VITE_APP_VERSION', ''),
  /**
   * API origin. Dev default: local API.
   * - `VITE_API_BACKEND=cloud_run` (default): production builds use same-origin `/api/**` (Firebase → Cloud Run).
   *   Optionally set this URL so chat streaming only can bypass Hosting for long streams.
   * - `VITE_API_BACKEND=external`: required in production builds; browser calls this URL directly (AWS ALB, etc.).
   */
  VITE_API_BASE_URL: apiBaseUrl,
  /**
   * Trust Indicators M2M API base (``/v1`` mount on the browser API by default).
   * Used for Copy API URL — e.g. ``http://localhost:8000/v1``.
   * Optional dedicated host later: set to that origin (no path) if running
   * ``external_api.main`` alone. Defaults to ``{VITE_API_BASE_URL}/v1``.
   */
  VITE_EXTERNAL_API_BASE_URL: externalApiBaseUrl,
  /** `cloud_run` (default) or `external` — see resolveApiBackend in auth.ts */
  VITE_API_BACKEND: getEnvVar('VITE_API_BACKEND', 'cloud_run'),
  VITE_LOGO_API_KEY: getEnvVar('VITE_LOGO_API_KEY'),
  VITE_FIREBASE_API_KEY: getEnvVar('VITE_FIREBASE_API_KEY'),
  VITE_FIREBASE_AUTH_DOMAIN: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN'),
  VITE_FIREBASE_PROJECT_ID: getEnvVar('VITE_FIREBASE_PROJECT_ID'),
  VITE_FIREBASE_STORAGE_BUCKET: getEnvVar('VITE_FIREBASE_STORAGE_BUCKET'),
  VITE_FIREBASE_MESSAGING_SENDER_ID: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  VITE_FIREBASE_APP_ID: getEnvVar('VITE_FIREBASE_APP_ID'),
  VITE_FIREBASE_MEASUREMENT_ID: getEnvVar('VITE_FIREBASE_MEASUREMENT_ID'),
  VITE_SUPABASE_URL: getEnvVar('VITE_SUPABASE_URL'),
  VITE_SUPABASE_ANON_KEY: getEnvVar('VITE_SUPABASE_ANON_KEY'),
  VITE_NANGO_CONNECT_URL: getEnvVar('VITE_NANGO_CONNECT_URL'),
  VITE_NANGO_API_URL: getEnvVar('VITE_NANGO_API_URL'),
  VITE_NANGO_PUBLIC_KEY: getEnvVar('VITE_NANGO_PUBLIC_KEY'),
  VITE_STRIPE_PUBLISHABLE_KEY: getEnvVar('VITE_STRIPE_PUBLISHABLE_KEY'),
  /** B2B-only: Standard, Pro, Enterprise. No B2C/legacy plan price IDs (Starter, Premium, Ultimate) are used. */
  VITE_PRO_PRICE_ID_MONTHLY: getEnvVar('VITE_PRO_PRICE_ID_MONTHLY'),
  VITE_PRO_PRICE_ID_ANNUAL: getEnvVar('VITE_PRO_PRICE_ID_ANNUAL'),
  VITE_STANDARD_PRICE_ID_MONTHLY: getEnvVar('VITE_STANDARD_PRICE_ID_MONTHLY'),
  VITE_STANDARD_PRICE_ID_ANNUAL: getEnvVar('VITE_STANDARD_PRICE_ID_ANNUAL'),
  VITE_ENTERPRISE_PRICE_ID_MONTHLY: getEnvVar('VITE_ENTERPRISE_PRICE_ID_MONTHLY'),
  VITE_ENTERPRISE_PRICE_ID_ANNUAL: getEnvVar('VITE_ENTERPRISE_PRICE_ID_ANNUAL'),
  VITE_MODEL_UPGRADE_GEMINI_MONTHLY: getEnvVar('VITE_MODEL_UPGRADE_GEMINI_MONTHLY'),
  VITE_MODEL_UPGRADE_GEMINI_ANNUAL: getEnvVar('VITE_MODEL_UPGRADE_GEMINI_ANNUAL'),
  VITE_MODEL_UPGRADE_ANTHROPIC_MONTHLY: getEnvVar('VITE_MODEL_UPGRADE_ANTHROPIC_MONTHLY'),
  VITE_MODEL_UPGRADE_ANTHROPIC_ANNUAL: getEnvVar('VITE_MODEL_UPGRADE_ANTHROPIC_ANNUAL'),
  VITE_MODEL_UPGRADE_COHERE_MONTHLY: getEnvVar('VITE_MODEL_UPGRADE_COHERE_MONTHLY'),
  VITE_MODEL_UPGRADE_COHERE_ANNUAL: getEnvVar('VITE_MODEL_UPGRADE_COHERE_ANNUAL'),
  VITE_MODEL_UPGRADE_GROK_MONTHLY: getEnvVar('VITE_MODEL_UPGRADE_GROK_MONTHLY'),
  VITE_MODEL_UPGRADE_GROK_ANNUAL: getEnvVar('VITE_MODEL_UPGRADE_GROK_ANNUAL'),
  // Okta SSO configuration
  VITE_OKTA_DOMAIN: getEnvVar('VITE_OKTA_DOMAIN'),
  VITE_OKTA_CLIENT_ID: getEnvVar('VITE_OKTA_CLIENT_ID'),
  VITE_OKTA_AUTH_SERVER: getEnvVar('VITE_OKTA_AUTH_SERVER', 'default'),
  VITE_OKTA_REDIRECT_URI: getEnvVar('VITE_OKTA_REDIRECT_URI'),
  /** Environment: development, staging, or production. Single source of truth for env detection. */
  VITE_ENV: getEnvVar('VITE_ENV', 'development'),
  DEV: getEnvVar('DEV') === 'true',
  PROD: getEnvVar('PROD') === 'true',
  /** Optional URL for retention policy (help centre / legal). Default in-app path used if unset. */
  VITE_RETENTION_POLICY_URL: getEnvVar('VITE_RETENTION_POLICY_URL'),
  /** Optional help center URL; when unset, Help & support uses the default help center URL from contact constants. */
  VITE_HELP_CENTER_URL: getEnvVar('VITE_HELP_CENTER_URL'),
  /**
   * Optional: full replacement string for the Google Drive card “data access” trust blurb (policy-approved copy).
   * When unset, `constants/legal/googleDriveDataAccess` default is used. Lets legal/ops change wording per env
   * without a code edit; long-term, prefer a CMS or public-config API as the source of truth.
   */
  VITE_GOOGLE_DRIVE_DATA_ACCESS_SUMMARY: getEnvVar('VITE_GOOGLE_DRIVE_DATA_ACCESS_SUMMARY'),
  /**
   * Comma-separated list of email domains to exclude from Firebase/GA4 analytics tracking.
   * Example: "karbondigital.com,karbondigital.co"
   */
  VITE_ANALYTICS_EXCLUDED_EMAIL_DOMAINS: getEnvVarAllowEmpty(
    'VITE_ANALYTICS_EXCLUDED_EMAIL_DOMAINS',
    'karbondigital.com',
  ),
  /** Post-onboarding tutorial kill switch / rollout (see `isPostOnboardingTutorialEnabled`). */
  VITE_POST_ONBOARDING_TUTORIAL_ENABLED: getEnvVar('VITE_POST_ONBOARDING_TUTORIAL_ENABLED', ''),
  VITE_POST_ONBOARDING_TUTORIAL_ROLLOUT_PERCENT: getEnvVar(
    'VITE_POST_ONBOARDING_TUTORIAL_ROLLOUT_PERCENT',
    '100',
  ),
};

// Environment detection helper functions (all based on VITE_ENV)
/**
 * Check if the current environment is development
 */
export const isDevelopment = (): boolean => {
  return ENV.VITE_ENV === 'development' || ENV.DEV || (!ENV.PROD && ENV.VITE_ENV !== 'staging');
};

/**
 * Check if the current environment is staging
 */
export const isStaging = (): boolean => {
  return ENV.VITE_ENV === 'staging';
};

/**
 * Check if the current environment is production
 */
export const isProduction = (): boolean => {
  return ENV.PROD || ENV.VITE_ENV === 'production';
};

/**
 * Check if the current environment is production or staging
 */
export const isProductionOrStaging = (): boolean => {
  return isStaging() || isProduction();
};

/**
 * Check if verification code is required for registration
 * Required in staging/production, optional in development
 */
export const requiresVerificationCode = (): boolean => {
  return isProductionOrStaging();
};

/**
 * Check if @karbondigital.com email restriction is enforced
 * Enforced in development only
 */
export const requiresKarbondigitalEmail = (): boolean => {
  return isDevelopment();
};
